/* global Word */

export const TO_SKIP = new Set([" ", "v", "v.", "vs", "vs."]);
const SEPARATOR_CHARS = new Set([" ", "\u00A0", "\r", "\f", "\t"]);
const SEPARATOR_LIST = [" ", "\u00A0", "\r", "\f", "\t"];

export function sanitizeTitle(titleText: string): string {
  // Sanitizes extracted title text.
  return (titleText || "")
    .replace(/^[.\s,\u00A0;")]+/, "")
    .replace(/[,\s\u00A0;]+$/, "")
    .replace(/\s+/g, " ");
}

function normalizeUnderline(
  underlineValue: Word.UnderlineType | "Mixed" | "None" | null | undefined
): Word.UnderlineType {
  // Normalizes underline values to a concrete Word.UnderlineType.
  if (underlineValue == null) return Word.UnderlineType.none;
  if (underlineValue === "Mixed" || underlineValue === "None") return Word.UnderlineType.none;
  return underlineValue;
}

function isTokenSkippable(tokenLowercase: string): boolean {
  // Checks whether a token should be skipped for stop-decision.
  return TO_SKIP.has(tokenLowercase);
}

type PreparedToken = {
  text: string;
  startIndex: number;
  endIndex: number;
  isItalic: boolean;
  isUnderlined: boolean;
  trimmedLowercase: string;
  shouldSkipToken: boolean;
};

export type PreparedParagraphTitleData = {
  tokens: PreparedToken[];
};

export async function prepareParagraphTitleData(
  context: Word.RequestContext,
  paragraph: Word.Paragraph,
  paragraphText: string
): Promise<PreparedParagraphTitleData> {
  // Prepares per-token formatting and offsets once for a paragraph.
  const paragraphRange = paragraph.getRange();

  const wordSeparatedRanges = paragraphRange.getTextRanges(SEPARATOR_LIST, true);
  wordSeparatedRanges.load("items/text,items/font/italic,items/font/underline");
  await context.sync();

  // Precompute token positions in paragraphText using the same separator characters
  // used for getTextRanges to avoid ambiguous indexOf-based matching.
  const separatorChars = SEPARATOR_CHARS;
  const textTokenPositions: { startIndex: number; endIndex: number }[] = [];
  let i = 0;
  while (i < paragraphText.length) {
    // Skip separators.
    while (i < paragraphText.length && separatorChars.has(paragraphText[i])) i++;
    if (i >= paragraphText.length) break;

    const tokenStart = i;
    while (i < paragraphText.length && !separatorChars.has(paragraphText[i])) i++;
    const tokenEnd = i;

    if (tokenEnd > tokenStart)
      textTokenPositions.push({ startIndex: tokenStart, endIndex: tokenEnd });
  }

  const tokens: PreparedToken[] = [];
  let textTokenIndex = 0;

  for (let rangeIndex = 0; rangeIndex < wordSeparatedRanges.items.length; rangeIndex++) {
    const rangeText = wordSeparatedRanges.items[rangeIndex].text || "";
    if (!rangeText) continue;

    if (textTokenIndex >= textTokenPositions.length) break;
    const tokenPos = textTokenPositions[textTokenIndex++];

    const rawTokenText = rangeText;
    const trimmedTokenText = rawTokenText.trim();
    const trimmedTokenLowercase = trimmedTokenText.toLowerCase();

    const italicValue = wordSeparatedRanges.items[rangeIndex].font.italic as
      | boolean
      | null
      | undefined;
    // In Office.js, null commonly means "Mixed". Treat that as italic-present.
    const isTokenItalic = italicValue === true || italicValue === null;

    const underlineValue = wordSeparatedRanges.items[rangeIndex].font.underline as
      | Word.UnderlineType
      | "Mixed"
      | "None"
      | null
      | undefined;

    // If Mixed, treat as underlined-present; otherwise normalize and compare to none.
    const isTokenUnderlined =
      underlineValue === "Mixed" || normalizeUnderline(underlineValue) !== Word.UnderlineType.none;

    const shouldSkipToken = trimmedTokenText === "" || isTokenSkippable(trimmedTokenLowercase);

    tokens.push({
      text: rawTokenText,
      startIndex: tokenPos.startIndex,
      endIndex: tokenPos.endIndex,
      isItalic: isTokenItalic,
      isUnderlined: isTokenUnderlined,
      trimmedLowercase: trimmedTokenLowercase,
      shouldSkipToken,
    });
  }

  return { tokens };
}

export function extractTitleForMatchPrepared(
  matchStartIndexInParagraph: number,
  prepared: PreparedParagraphTitleData
): string {
  // Extracts title using prepared token/style data (no Office.js calls).
  const tokens = prepared.tokens;

  if (matchStartIndexInParagraph <= 0 || tokens.length === 0) return "";

  // Binary search: find the last token whose endIndex <= matchStartIndexInParagraph.
  // Tokens are sorted by startIndex (monotonically increasing), so binary search applies.
  let tokenIndexLeftOfMatch = -1;
  let lo = 0;
  let hi = tokens.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    if (tokens[mid].endIndex <= matchStartIndexInParagraph) {
      tokenIndexLeftOfMatch = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }

  if (tokenIndexLeftOfMatch < 0) return "";

  const collectedTokenTexts: string[] = [];
  let hasStartedCollecting = false;
  let lockedStyleMode: "italic" | "underline" | null = null;

  while (tokenIndexLeftOfMatch >= 0) {
    // Walk left collecting styled tokens and counting skippable bridge tokens once started.
    const currentToken = tokens[tokenIndexLeftOfMatch];

    const rawTokenText = currentToken.text;

    const isTokenItalic = currentToken.isItalic;
    const isTokenUnderlined = currentToken.isUnderlined;

    const shouldSkipToken = currentToken.shouldSkipToken;

    if (!hasStartedCollecting && shouldSkipToken) {
      tokenIndexLeftOfMatch--;
      continue;
    }

    if (!hasStartedCollecting) {
      if (isTokenItalic) {
        lockedStyleMode = "italic";
        hasStartedCollecting = true;
        collectedTokenTexts.push(rawTokenText);
        tokenIndexLeftOfMatch--;
        continue;
      }

      if (isTokenUnderlined) {
        lockedStyleMode = "underline";
        hasStartedCollecting = true;
        collectedTokenTexts.push(rawTokenText);
        tokenIndexLeftOfMatch--;
        continue;
      }

      break;
    }

    if (shouldSkipToken) {
      collectedTokenTexts.push(rawTokenText);
      tokenIndexLeftOfMatch--;
      continue;
    }

    const doesTokenMatchLockedStyle =
      (lockedStyleMode === "italic" && isTokenItalic) ||
      (lockedStyleMode === "underline" && isTokenUnderlined);

    if (!doesTokenMatchLockedStyle) break;

    collectedTokenTexts.push(rawTokenText);
    tokenIndexLeftOfMatch--;
  }

  if (collectedTokenTexts.length > 0) {
    return sanitizeTitle(collectedTokenTexts.reverse().join(" "));
  }

  return "";
}

/**
 * Backwards-compatible entrypoint (keeps your scanner call sites working).
 * Internally prepares per-paragraph data once if you pass it in yourself.
 */
export async function extractTitleForMatch(
  context: Word.RequestContext,
  paragraph: Word.Paragraph,
  paragraphText: string,
  matchStartIndexInParagraph: number
): Promise<string> {
  // Extracts the styled title immediately preceding the match start index.
  try {
    const prepared = await prepareParagraphTitleData(context, paragraph, paragraphText);
    return extractTitleForMatchPrepared(matchStartIndexInParagraph, prepared);
  } catch {
    // If any error occurs while accessing Word API properties or processing tokens,
    // return empty to avoid breaking the caller.
    return "";
  }
}
